/* placementPipeline.js — the drop/commit placement glue (WI-1).
 *
 * Ported from lhc-app.html:
 *   ruleAllowPlacement (~32213)  hard refusal via validatePlacement + first-warning toast
 *   checkTiltUp        (~32221)  R1-4 tall-unit diagonal vs ceiling advisory
 *   checkDishwasherSink(~32233)  R1-3 dishwasher far from any sink advisory
 *   checkDoorWidthSplit(~32254)  R3-2 single door leaf > 600mm advisory (+ 2-door alt SKU)
 *   checkSinkWindow    (~32281)  F-3 sink under a window advisory
 *   ruleAdvise         (~32300)  non-blocking advisories AFTER a successful commit
 *   maybeAutoCorner    (~32451)  P7 auto corner unit, BOTH cornerStyle variants (CV1
 *                                simple = L-corner L/R, shell = generic 600 host),
 *                                roomSensed + faceSign inheritance
 *   resolveWallOverlap (~32516)  occupied? slide to the nearest free gap on THAT wall
 *   svgDoorCount       (~34112)  door count from the SKU code (needed by R3-2)
 *   getBackWall        (~31195)  back-wall fallback for the tilt-up wall lookup
 *   CV5_APPLIANCE_VARIANTS (~46253) + the appliance-picker hook (~36556, openAppliancePicker
 *                                ~46266 — CV6: appliance always centered, offset 0)
 *   placeCabinetAt     (36527–36566 tap/drop commit path, in the EXACT call order:
 *                                snapSides(150) → snapToCornerRuns(0.15) → resolveWallOverlap
 *                                → snapSides(40) → ruleAllowPlacement → push → maybeAutoCorner
 *                                → appliance-picker hook → ruleAdvise → "Placed:" toast;
 *                                + v0.32 RULE B2 far-drop refusal and the partition/peninsula
 *                                faceSign pick from the library-drop path, 46614–46634)
 *
 * Engine semantics preserved (LOCK parity — do NOT modernize):
 *   • resolveWallOverlap and placeCabinetAt MUTATE the placement / state in place, exactly
 *     like the prototype (in-place mutation + the caller's version counter).
 *   • Toast strings are returned VERBATIM in { reason / messages / toast } so the UI can
 *     show the engine's exact wording; this module never touches the DOM.
 *   • ruleAdvise wraps all four checks in ONE try/catch (an advisory throw aborts the
 *     remaining checks but never the placement) — same as the original.
 */
import { LHC_RULES, RULE_EPS, DEFAULT_WALL_HEIGHT } from "./constants";
import { getWallPlacementInfo, placementAABB } from "./placement";
import { getWallGraph } from "./wallGraph";
import { snapToNearestWall, snapSides, wallInnerSpan } from "./snapping";
import { snapToCornerRuns, blockedIntervals, snapExactFit } from "./contact";
import { validatePlacement } from "./validation";
import { openingAABB } from "./openings";
import { effDef } from "./pricing";
import { snapIslandXZ } from "./islandSnap";
import { quantMm } from "./precision";
import { stackElevationFor, applyStack } from "./stackLaw";

// Engine-format ids for pipeline-created placements ('pl_' + Date.now() + '_' + rand, ~36527;
// auto-corner uses the 'pl_ac_' prefix, ~32494).
function pipelineId(prefix) {
  return prefix + Date.now() + "_" + Math.floor(Math.random() * 1000);
}

/** getBackWall(walls): the horizontal wall with the smallest mid-z, or null. (~31195) */
export function getBackWall(walls) {
  if (!walls || walls.length === 0) return null;
  let back = null, backZ = Infinity;
  walls.forEach((w) => {
    const isHorizontal = Math.abs(w.start[1] - w.end[1]) < 0.05;
    if (!isHorizontal) return;
    const mz = (w.start[1] + w.end[1]) / 2;
    if (mz < backZ) { backZ = mz; back = w; }
  });
  return back;
}

/** svgDoorCount(code): door count parsed from the SKU code — 'DR' prefix/marker = drawers,
 *  zero doors; else D<n>; default 1. (~34112) */
export function svgDoorCount(code) {
  if (!code) return 1;
  const c = String(code).toUpperCase();
  if (c.indexOf("DR-") === 0 || /DR\d/.test(c)) return 0;
  const m = c.match(/D(\d)/);
  return m ? parseInt(m[1], 10) : 1;
}

/** ruleAllowPlacement(candidate, state, lib): the hard gate before pushing a placement.
 *  Refusal returns { ok:false, reason } (the engine toasts the reason); success carries the
 *  first validation warning (the engine toasts warnings[0]). (~32213) */
export function ruleAllowPlacement(candidate, state, lib) {
  const v = validatePlacement(candidate, state.placements, lib, state.walls, state.kitchenWizard, state.wallOpenings);
  if (!v.ok) return { ok: false, reason: v.reason };
  return { ok: true, warning: v.warnings && v.warnings.length ? v.warnings[0] : null };
}

/** R1-4 tilt-up: a tall unit whose diagonal exceeds the ceiling can't be stood up. (~32221)
 *  Returns the advisory message or null. */
export function checkTiltUp(candidate, state, lib) {
  const def = lib[candidate.type];
  if (!def || def.role !== "cabinet-tall") return null;
  const wall = candidate.wallId ? state.walls.find((w) => w.id === candidate.wallId) : getBackWall(state.walls);
  const wallH = (wall && wall.height) || DEFAULT_WALL_HEIGHT;
  const diag = Math.hypot(def.height, def.depth);
  if (diag > wallH - LHC_RULES.TILT_MARGIN) {
    return `${def.code || def.name} can't be tilted upright under this ${wallH.toFixed(2)}m ceiling — reduce height or depth.`;
  }
  return null;
}

/** R1-3 advisory: dishwasher far (> CORRIDOR_MIN) from any sink → plumbing warning. (~32233) */
export function checkDishwasherSink(candidate, state, lib) {
  const def = lib[candidate.type];
  if (!def || def.subtype !== "dishwasher") return null;
  const cBox = placementAABB(candidate, effDef(candidate, lib), state.walls);
  if (!cBox) return null;
  let nearest = Infinity;
  state.placements.forEach((other) => {
    if (other.id === candidate.id) return;
    const od = lib[other.type];
    if (!od || od.subtype !== "sink") return;
    const oBox = placementAABB(other, effDef(other, lib), state.walls);
    if (!oBox) return;
    nearest = Math.min(nearest, Math.hypot(cBox.cx - oBox.cx, cBox.cz - oBox.cz));
  });
  if (isFinite(nearest) && nearest > LHC_RULES.CORRIDOR_MIN) {
    return `Dishwasher is ${nearest.toFixed(1)}m from the nearest sink — check the plumbing run.`;
  }
  return null;
}

/** F-3 advisory: a sink placed under a window (AABB overlap in X and Z). One message per
 *  matching window, like the original's per-window toast. (~32281) */
export function checkSinkWindow(candidate, state, lib) {
  const out = [];
  const def = lib[candidate.type];
  if (!def || def.subtype !== "sink") return out;
  const cBox = placementAABB(candidate, effDef(candidate, lib), state.walls);
  if (!cBox) return out;
  const opens = Array.isArray(state.wallOpenings) ? state.wallOpenings : [];
  for (const op of opens) {
    if (op.type !== "window" || op.wallId !== candidate.wallId) continue;
    const oBox = openingAABB(op, state.walls);
    if (!oBox) continue;
    const xOverlap = cBox.minX < oBox.maxX - RULE_EPS && cBox.maxX > oBox.minX + RULE_EPS;
    const zOverlap = cBox.minZ < oBox.maxZ - RULE_EPS && cBox.maxZ > oBox.minZ + RULE_EPS;
    if (xOverlap && zOverlap) {
      out.push("Sink positioned under the window — check tap height and extractor/blind clearances.");
    }
  }
  return out;
}

/** R3-2: door-split advisory — a single leaf wider than 600mm; recommends a 2-door SKU at
 *  the same carcass width when one exists. (~32254) */
export function checkDoorWidthSplit(candidate, state, lib) {
  const def = lib[candidate.type];
  if (!def || def.role === "appliance") return null;
  const doorCount = svgDoorCount(candidate.type);
  if (doorCount < 1) return null;
  // Mirror the 3D-front leaf-width formula: subtract 20mm reveal gap.
  const leafW = (def.width - 0.020) / doorCount;
  if (leafW <= LHC_RULES.DOOR_WIDTH_SPLIT) return null;
  // Look for a matching 2-door SKU at the same carcass width (nice-to-have).
  let altCode = null;
  for (const [k, d] of Object.entries(lib)) {
    if (k === candidate.type) continue;
    if (d.role !== def.role) continue;
    if (Math.abs(d.width - def.width) > 0.005) continue;
    if (svgDoorCount(k) >= 2) { altCode = d.code || k; break; }
  }
  const hint = altCode ? ` — consider ${altCode} instead` : " — recommend 2-door variant for hinge load and swing clearance";
  return `Door front ${Math.round(leafW * 1000)}mm exceeds ${Math.round(LHC_RULES.DOOR_WIDTH_SPLIT * 1000)}mm${hint}`;
}

/** Non-blocking advisories — call AFTER a successful commit. Returns the toast messages in
 *  the original call order; one shared try/catch (an advisory must never break placement).
 *  (~32300) */
export function ruleAdvise(candidate, state, lib) {
  const messages = [];
  try {
    const tilt = checkTiltUp(candidate, state, lib);
    if (tilt) messages.push(tilt);
    const dw = checkDishwasherSink(candidate, state, lib);
    if (dw) messages.push(dw);
    checkSinkWindow(candidate, state, lib).forEach((m) => messages.push(m));
    const split = checkDoorWidthSplit(candidate, state, lib);
    if (split) messages.push(split);
  } catch (e) { /* advisory errors are swallowed, exactly like the original */ }
  return messages;
}

/** If p overlaps a same-layer cabinet on its wall, slide it to the nearest free gap on THAT
 *  wall (the user chose the wall — keep it). No gap → leave as-is and let ruleAllowPlacement
 *  refuse with its toast. MUTATES p.absLeft (engine semantics). (~32516) */
export function resolveWallOverlap(p, def, placements, lib, walls) {
  if (!def || !p.wallId || p.island) return;
  const B = blockedIntervals(p, def, placements, lib, walls);
  if (!B.len) return;
  // Signed span bounds (not a 0 floor): minLeft is negative at a convex corner —
  // the flush-to-visible-corner position is a legal free-gap candidate.
  const wallRW = walls.find((w) => w.id === p.wallId);
  const spRW = wallRW ? wallInnerSpan(wallRW, walls) : { off0: 0, off1: 0 };
  const width = def.width, minLeft = spRW.off0, maxLeft = B.len - spRW.off1 - width;
  const t0 = Math.max(minLeft, Math.min(p.absLeft, maxLeft));
  const blocked = B.intervals.some((iv) => t0 > iv[0] + 1e-6 && t0 < iv[1] - 1e-6);
  if (!blocked) { p.absLeft = t0; return; }
  // candidate free points: interval edges (clamped), pick nearest to desired
  const cands = [minLeft, maxLeft];
  B.intervals.forEach((iv) => { cands.push(iv[0], iv[1]); });
  let best = null, bestCost = Infinity;
  cands.forEach((tc) => {
    const t = Math.max(minLeft, Math.min(tc, maxLeft));
    const free = !B.intervals.some((iv) => t > iv[0] + 1e-6 && t < iv[1] - 1e-6);
    if (!free) return;
    const cost = Math.abs(t - t0);
    if (cost < bestCost) { bestCost = cost; best = t; }
  });
  if (best !== null) p.absLeft = best;
}

/** P7 (LH rulebook): auto corner unit when two base runs meet at a corner. Checks BOTH wall
 *  ends; pushes companions into state.placements (in-place, engine semantics) and returns
 *  { added: [placement...], messages: [toast...] }. CV1 corner style from
 *  state.kitchenWizard.cornerStyle: 'simple' = L-corner L/R by end, 'shell' = first generic
 *  600 base host. Inherits roomSensed (v0.31a) and faceSign (v0.35). Must never throw. (~32451) */
export function maybeAutoCorner(p, state, lib) {
  const result = { added: [], messages: [] };
  try {
    const def = effDef(p, lib);
    if (!def || def.role !== "cabinet-base" || p.island || !p.wallId) return result;
    if ((def.subcategory || "") === "Corner") return result; // don't chain corners
    const wall = state.walls.find((w) => w.id === p.wallId);
    const info = wall && getWallPlacementInfo(wall, state.walls);
    if (!info) return result;
    [["start", 0], ["end", 1]].forEach(([endName, endIdx]) => {
      // p must be near this end of its wall
      const pEdge = endIdx === 0 ? p.absLeft : info.len - (p.absLeft + def.width);
      if (pEdge > 1.0) return;
      // another wall shares this corner? v0.52 -> 2026-09-28: the wall graph's NODE
      // MEMBERSHIP (the same source wallFaceSpan/collision use) — previously a
      // private 1 cm check, then jointsCoincide on raw endpoints; in an irregular
      // room whose corner endpoints differ by 1–20 cm (and every leg-per-commit
      // corner, 106 mm) the span treated the joint as a corner but the auto corner
      // unit silently never fired (predicate disagreement, BUG A family).
      const _gw = getWallGraph(state.walls).walls.get(wall.id);
      const _nbs = _gw ? (endIdx === 0 ? _gw.neighborsAtStart : _gw.neighborsAtEnd) : [];
      const _nb = _nbs.find((nb) => nb.wallId !== wall.id && state.walls.some((w2) => w2.id === nb.wallId));
      const other = _nb ? state.walls.find((w2) => w2.id === _nb.wallId) : null;
      if (!other) return;
      const oInfo = getWallPlacementInfo(other, state.walls);
      if (!oInfo) return;
      const otherCornerAtStart = _nb.end === "start";
      // a base run on the other wall near the same corner?
      const hasRun = state.placements.some((q) => {
        if (q.island || (q.wallId || null) !== other.id) return false;
        const qd = effDef(q, lib);
        if (!qd || qd.role !== "cabinet-base") return false;
        const qEdge = otherCornerAtStart ? q.absLeft : oInfo.len - (q.absLeft + qd.width);
        return qEdge < 1.0;
      });
      if (!hasRun) return;
      // CV1: corner style — 'simple' = L-corner unit, 'shell' = generic 600 host
      const style = (state.kitchenWizard && state.kitchenWizard.cornerStyle) || "simple";
      let type;
      if (style === "shell") {
        type = Object.keys(lib).find((k) => {
          const d = lib[k];
          return d && d.role === "cabinet-base" && (d.subcategory || "") !== "Corner" && Math.abs(d.width - 0.6) < 0.01;
        }) || "cb-d1s1-90-l";
      } else {
        type = endIdx === 0 ? "cb-d1s1-90-l" : "cb-d1s1-90-r";
      }
      const cdef = lib[type] || lib["cb-d1s1-90-l"];
      if (!cdef) return;
      if (info.len < cdef.width) return;
      const cand = {
        id: pipelineId("pl_ac_"),
        type: lib[type] ? type : "cb-d1s1-90-l",
        wallId: wall.id,
        absLeft: (function () {
          const _s = wallInnerSpan(wall, state.walls);
          return endIdx === 0 ? _s.off0 : info.len - _s.off1 - cdef.width;
        })(),
      };
      // corner void must be genuinely free
      const B = blockedIntervals(cand, cdef, state.placements, lib, state.walls);
      const t = cand.absLeft;
      if (B.intervals.some((iv) => t > iv[0] + 1e-6 && t < iv[1] - 1e-6)) return;
      const v = validatePlacement(cand, state.placements, lib, state.walls, state.kitchenWizard, state.wallOpenings);
      if (!v.ok) return;
      cand.roomSensed = !!p.roomSensed; // v0.31a: corner companion inherits room-sense
      if (p.faceSign !== undefined) cand.faceSign = p.faceSign; // v0.35: same side on double-sided walls
      state.placements.push(cand);
      result.added.push(cand);
      result.messages.push("Corner unit added automatically: " + cdef.name);
    });
  } catch (e) { /* auto-corner must never break placement */ }
  return result;
}

// ── CV5+CV6 (rulebook): appliance binding ──────────────────────────────────
// Hosts that need an embedded appliance pick. Map of host-subtype → variants. (~46253)
export const CV5_APPLIANCE_VARIANTS = {
  sink: [
    { id: "single-bowl", label: "Single bowl", priceDelta: 120 },
    { id: "double-bowl", label: "Double bowl", priceDelta: 180 },
    { id: "one-half-bowl", label: "1.5 bowl + drainer", priceDelta: 220 },
  ],
  oven: [
    { id: "oven-single", label: "Single oven", priceDelta: 380 },
    { id: "oven-double", label: "Double oven + warmer", priceDelta: 720 },
    { id: "oven-steam", label: "Steam oven combo", priceDelta: 950 },
  ],
};

/** The appliance-picker hook (CV5, ~36556 + openAppliancePicker ~46266): if the freshly
 *  placed host has appliance variants, seed the CV6 centered default on p and return the
 *  picker payload for the UI modal — else null. MUTATES p.appliance (engine semantics). */
export function appliancePickerFor(p, lib) {
  const def = effDef(p, lib);
  if (!def || !def.subtype) return null;
  const variants = CV5_APPLIANCE_VARIANTS[def.subtype];
  if (!variants) return null;
  p.appliance = p.appliance || { variantId: variants[0].id, offset: 0 }; // CV6: centered on host
  return { placementId: p.id, subtype: def.subtype, variants };
}

/** Commit a picker choice: CV6 — appliances always centered (offset 0). (~46294) */
export function applyApplianceChoice(p, variantId) {
  p.appliance = { variantId, offset: 0 };
}

/** placeCabinetAt(state, type, [wx, wz], lib, opts) — the FULL drop/commit pipeline in the
 *  engine's exact call order (36527–36566):
 *
 *    snapToNearestWall → (island branch | snapSides(150) → snapToCornerRuns(0.15) →
 *    resolveWallOverlap → snapSides(40)) → ruleAllowPlacement → placements.push →
 *    maybeAutoCorner → appliance-picker hook → ruleAdvise → "Placed:" toast
 *
 *  plus, from the library-drop path (46612–46634): v0.32 RULE B2 — a drop farther than
 *  snapDist (0.7m default) from every wall places NOTHING, and the v0.33 (LH C4) faceSign
 *  pick on partition/peninsula walls (the drop point picks the face).
 *
 *  opts: { snapDist = 0.7  (pass Infinity for tap-to-place — nearest wall always wins),
 *          island  = false (P1: free-standing at the tap point, oriented like the nearest
 *                           wall — window.placingIsland equivalent) }
 *
 *  Returns { ok:false, reason } on refusal (nothing placed — B2 or rules engine), or
 *  { ok:true, placement, warning, autoCorner:{added,messages}, appliancePicker, advice,
 *    toast } on success. Mutates state.placements in place (engine semantics); the caller
 *  owns undo snapshots (pushUndo BEFORE, ~36526) and the version bump. */
export function placeCabinetAt(state, type, world, lib, opts = {}) {
  const def = lib[type];
  if (!def) return { ok: false, reason: "Unknown module " + type };
  const wx = world[0], wz = world[1];

  // EXACT DROP (user mandate: the drop lands where the GREEN preview showed —
  // never relocated). opts.exact = { wallId, absLeft } from the live indicator:
  // no magnets, no corner-run snap, no resolveWallOverlap slide. The rules gate
  // still runs — a refusal places NOTHING (the caller already showed red).
  if (opts.exact && opts.exact.wallId != null) {
    const exP = { id: pipelineId("pl_"), type, wallId: opts.exact.wallId, absLeft: quantMm(opts.exact.absLeft), roomSensed: true };
    const exW = state.walls.find((w) => w.id === exP.wallId);
    if (exW && (exW.style === "partition" || exW.style === "peninsula")) {
      const _mx = (exW.start[0] + exW.end[0]) / 2, _mz = (exW.start[1] + exW.end[1]) / 2;
      const _dx = exW.end[0] - exW.start[0], _dz = exW.end[1] - exW.start[1];
      const _l = Math.hypot(_dx, _dz) || 1;
      exP.faceSign = ((wx - _mx) * (-_dz / _l) + (wz - _mz) * (_dx / _l)) >= 0 ? 1 : -1;
    }
    // STACK LAW (batch 9): the exact ghost may carry the level it was shown at; otherwise an
    // occupied span is tried above the units it overlaps before the rules judge it
    if (opts.exact.elevationMm != null) applyStack(exP, { elevationMm: opts.exact.elevationMm });
    else { const st = stackElevationFor(exP, def, state, lib); if (st) applyStack(exP, st); }
    const exAllow = ruleAllowPlacement(exP, state, lib);
    if (!exAllow.ok) return { ok: false, reason: exAllow.reason, placement: exP };
    state.placements.push(exP);
    return {
      ok: true, placement: exP, warning: exAllow.warning || null,
      autoCorner: maybeAutoCorner(exP, state, lib),
      appliancePicker: appliancePickerFor(exP, lib),
      advice: ruleAdvise(exP, state, lib),
      toast: `Placed: ${def.name}`, // same toast contract as the legacy path (36565)
    };
  }

  const snapDist = opts.snapDist === undefined ? 0.7 : opts.snapDist;
  const snap = snapToNearestWall(wx, wz, def.width, state.walls, snapDist);
  if (!snap) {
    // NO GHOST MOVE (2026-07-15 user mandate): cabinets ONLY snap to walls —
    // the auto island-adjacency drop is gone; free-standing units come solely
    // from the explicit island tool (opts.island above).
    // v0.32 RULE B2 (LH): no wall, no cabinet — a drop away from every wall places
    // nothing (46618). With zero walls the tap path says draw one first (36532).
    return {
      ok: false,
      reason: state.walls.length === 0
        ? "Draw a wall first — cabinets attach to walls"
        : "Drop the cabinet near a wall",
    };
  }
  const id = pipelineId("pl_");
  let newP;
  if (opts.island) {
    // P1 (LH rulebook): island — free-standing at the tap point, oriented like the
    // nearest wall (door faces the same way). (36536–36542)
    // 2026-07-14 CAD mandate (Issue 1): the drop point runs through the island
    // magnet — 0mm edge-to-edge contact / flush / center against neighbours —
    // and commits on the 1mm grid, so islands form without hacks.
    const wi = getWallPlacementInfo(snap.wall, state.walls);
    newP = { id, type, island: true, x: wx, z: wz,
             dirX: wi.dirX, dirZ: wi.dirZ, nx: wi.nx, nz: wi.nz,
             roomSensed: true };
    const snapped = snapIslandXZ(newP, def, wx, wz, state.placements, lib, state.walls);
    newP.x = quantMm(snapped.x);
    newP.z = quantMm(snapped.z);
  } else {
    newP = { id, type, absLeft: snap.absLeft, wallId: snap.wall.id, roomSensed: true };
    // v0.33 (LH C4): on a double-sided wall, the drop point picks the face. (46626–46634)
    const _sw = snap.wall;
    if (_sw.style === "partition" || _sw.style === "peninsula") {
      const _smx = (_sw.start[0] + _sw.end[0]) / 2, _smz = (_sw.start[1] + _sw.end[1]) / 2;
      const _sdx = _sw.end[0] - _sw.start[0], _sdz = _sw.end[1] - _sw.start[1];
      const _sl = Math.hypot(_sdx, _sdz) || 1;
      const _lpx = -_sdz / _sl, _lpz = _sdx / _sl;
      newP.faceSign = ((wx - _smx) * _lpx + (wz - _smz) * _lpz) >= 0 ? 1 : -1;
    }
    newP.absLeft = snapSides(newP, state.placements, lib, state.walls, undefined, 150); // P3: magnet to neighbors + wall ends (36545)
    newP.absLeft = snapToCornerRuns(newP, def, 0.15, state.placements, lib, state.walls); // P3: magnet to corner-run faces (36546)
    // TIGHT-GAP AUTO-SEAT (2026-07-14, Issue 4): a near-exact opening has ONE
    // legal position — drop straight into it. The seat is FINAL: the slide
    // resolver's merged intervals read a both-sides-touching position as
    // "blocked" and would wrongly evict it (the pixel-hunting bug).
    const seated = snapExactFit(newP, def, newP.absLeft, state.placements, lib, state.walls);
    if (seated !== newP.absLeft) {
      newP.absLeft = seated;
    } else {
      /* STACK LAW (batch 9, 2026-09-28 user: "the stock unit needs to go above the wall unit
       * without a problem if there is space — add a law"): an occupied span first tries the
       * level ABOVE the units it overlaps (engine/stackLaw); only when nothing fits up there
       * does the drop slide sideways to the nearest free spot as before. */
      const stacked = stackElevationFor(newP, def, state, lib);
      if (stacked) applyStack(newP, stacked);
      else {
        resolveWallOverlap(newP, def, state.placements, lib, state.walls); // occupied? slide to nearest free spot (36547)
        newP.absLeft = snapSides(newP, state.placements, lib, state.walls, undefined, 40); // seat flush after the nudge (36548)
      }
    }
    // CAD PRECISION (2026-07-17 browser-QA catch): the WALL drop path stored the
    // RAW cursor projection (absLeft 2.7923423…) — every other interactive
    // commit (drag, island drop) lands on the 1mm grid (Issue 5 contract).
    // Quantize the final drop too: stored == displayed, always whole mm.
    newP.absLeft = quantMm(newP.absLeft);
  }
  const allow = ruleAllowPlacement(newP, state, lib); // (36550)
  if (!allow.ok) {
    return { ok: false, reason: allow.reason, placement: newP }; // refused by rules engine — nothing placed
  }
  state.placements.push(newP); // (36553)
  const autoCorner = maybeAutoCorner(newP, state, lib); // (36554)
  // CV5: if the placed cabinet is a host with appliance variants, open the picker (36556–36560)
  const appliancePicker = appliancePickerFor(newP, lib);
  const advice = ruleAdvise(newP, state, lib); // (36564)
  return {
    ok: true,
    placement: newP,
    warning: allow.warning || null,
    autoCorner,
    appliancePicker,
    advice,
    toast: `Placed: ${def.name}`, // (36565)
  };
}
